// IncidentSummaryTable.tsx
import React from 'react';

export interface IncidentSummaryRow {
  description: string;
  count: number | null;
  remarks: string | null;
}

interface IncidentSummaryTableProps {
  data?: IncidentSummaryRow[];
  onChange?: (index: number, updatedRow: IncidentSummaryRow) => void;
  isEditable?: boolean;
  readOnlyCountIndices?: number[];
}

export default function IncidentSummaryTable({
  data = [],
  onChange,
  isEditable = true,
  readOnlyCountIndices = [],
}: IncidentSummaryTableProps) {
  const handleFieldChange = (
    idx: number,
    field: keyof IncidentSummaryRow,
    value: string | number | null
  ) => {
    if (!onChange || !isEditable) return;
    const row = data[idx];
    let processedValue: string | number | null = value;

    if (field === 'count') {
      // Block editing if this row's count is auto-filled
      if (readOnlyCountIndices.includes(idx)) return;
      if (typeof value === 'string') {
        if (value === '') {
          processedValue = null;
        } else {
          const numValue = parseFloat(value);
          processedValue = isNaN(numValue) ? null : numValue;
        }
      }
    }

    onChange(idx, {
      ...row,
      [field]: processedValue,
    });
  };

  const getDisplayValue = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '';
    return value.toString();
  };

  return (
    <div className="w-full overflow-x-auto mt-6">
      <div className="bg-[#48b0d4] text-white px-4 py-2 font-bold text-lg mb-0 rounded-t-lg">
        Incident Summary – for the Month (Yearly)
      </div>
      <table className="min-w-full border-collapse border border-gray-300 bg-white">
        <thead>
          <tr className="bg-gray-200 border-b-2 border-gray-400">
            <th className="border border-gray-300 px-4 py-2 text-left font-bold text-sm text-gray-800 w-1/2">
              Description
            </th>
            <th className="border border-gray-300 px-3 py-2 text-center font-bold text-sm text-gray-800 w-1/4">
              Count
            </th>
            <th className="border border-gray-300 px-4 py-2 text-left font-bold text-sm text-gray-800 w-1/4">
              Remarks (if any)
            </th>
          </tr>
        </thead>
        <tbody>
          {data.map((row, idx) => {
            const isCountReadOnly = readOnlyCountIndices.includes(idx);
            return (
              <tr key={idx} className="bg-white hover:bg-gray-50 border-b border-gray-300">
                <td className="border border-gray-300 px-4 py-2 text-sm text-gray-800 font-medium">
                  {row.description}
                </td>
                <td className="border border-gray-300 px-3 py-2 text-center">
                  {isEditable && !isCountReadOnly ? (
                    <input
                      type="number"
                      min="0"
                      value={getDisplayValue(row.count)}
                      onChange={(e) => handleFieldChange(idx, 'count', e.target.value)}
                      placeholder="Count"
                      className="w-full px-2 py-1 text-center border border-gray-400 rounded bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  ) : (
                    <div className="flex items-center justify-center gap-1.5">
                      <span className="text-sm text-gray-700 font-medium">
                        {row.count !== null ? row.count : '—'}
                      </span>
                      {isCountReadOnly && (
                        <span
                          title="Auto-calculated from Table III"
                          className="inline-flex items-center text-blue-400"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </span>
                      )}
                    </div>
                  )}
                </td>
                <td className="border border-gray-300 px-4 py-2">
                  {isEditable ? (
                    <textarea
                      value={row.remarks || ''}
                      onChange={(e) => handleFieldChange(idx, 'remarks', e.target.value || null)}
                      rows={2}
                      className="w-full px-2 py-1 border border-gray-400 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
                      placeholder="Remarks"
                    />
                  ) : (
                    <span className="text-sm text-gray-700 whitespace-pre-wrap break-words">
                      {row.remarks || ''}
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
